import React, { useMemo } from 'react';
import { Receipt, TrendingUp, TrendingDown } from 'lucide-react';
import { ProjectModel } from '../../../types/projects';
import { WorkOrder, WorkOrderStatus } from '../../../types/workOrders';
import { formatCurrency } from '../../../Helpers/FormatUtils';
import { sumAllocation } from '../../../Helpers/budgetAllocation';

interface ProjectCostsSectionProps {
    project: ProjectModel;
    linkedWorkOrders: WorkOrder[];
}

export const ProjectCostsSection: React.FC<ProjectCostsSectionProps> = ({
    project,
    linkedWorkOrders
}) => {
    // Calculate budget summary
    const workOrderCostsTotal = useMemo(() =>
        linkedWorkOrders
            .filter(wo => wo.status !== WorkOrderStatus.OPEN && wo.actualCost !== undefined)
            .reduce((sum, wo) => sum + (wo.actualCost ?? 0), 0),
        [linkedWorkOrders]
    );

    const totalActualCost = workOrderCostsTotal;
    const budgetTotal = sumAllocation(project.budget);
    const hasBudget = budgetTotal > 0;
    const remainingBudget = hasBudget ? budgetTotal - totalActualCost : undefined;
    const isOverBudget = remainingBudget !== undefined && remainingBudget < 0;

    // Show nothing if no budget and no work orders with costs
    const hasAnyCosts = workOrderCostsTotal > 0;
    const showBudgetSummary = hasBudget || hasAnyCosts;

    if (!showBudgetSummary) {
        return null;
    }

    return (
        <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #dee2e6'
        }}>
            {/* Budget Summary */}
            <div style={{
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
            }}>
                <h6 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Receipt size={18} />
                    Budget vs Faktisk kostnad
                </h6>

                <div style={{ display: 'grid', gap: '8px' }}>
                    {hasBudget && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#666' }}>Budget:</span>
                            <span style={{ fontWeight: 500 }}>{formatCurrency(budgetTotal)}</span>
                        </div>
                    )}

                    {workOrderCostsTotal > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#666' }}>Ärenden:</span>
                            <span>{formatCurrency(workOrderCostsTotal)}</span>
                        </div>
                    )}

                    {hasAnyCosts && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            borderTop: '1px solid #dee2e6',
                            paddingTop: '8px',
                            marginTop: '4px'
                        }}>
                            <span style={{ fontWeight: 600 }}>Totalt:</span>
                            <span style={{ fontWeight: 600 }}>{formatCurrency(totalActualCost)}</span>
                        </div>
                    )}

                    {remainingBudget !== undefined && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingTop: '8px'
                        }}>
                            <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                color: isOverBudget ? '#dc3545' : '#198754'
                            }}>
                                {isOverBudget ? (
                                    <>
                                        <TrendingUp size={16} />
                                        Över budget:
                                    </>
                                ) : (
                                    <>
                                        <TrendingDown size={16} />
                                        Kvar av budget:
                                    </>
                                )}
                            </span>
                            <span style={{
                                fontWeight: 600,
                                color: isOverBudget ? '#dc3545' : '#198754'
                            }}>
                                {formatCurrency(Math.abs(remainingBudget))}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
